import React from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../../components/ui/FormField';
import type { SchemaFieldProps } from '../types';

/**
 * Custom SchemaForm widget for an event template's `firstOccurrence`. Renders a
 * native date input (value is a YYYY-MM-DD string, matching the legacy form's
 * date handling) plus the recurrence-preview helper text that depends on the
 * sibling `recurringPattern` value (read from the full form `values`).
 *
 * Extracted verbatim (behaviour-wise) from EventTemplateForm's former inline
 * firstOccurrence block + getRecurrenceDescription helper.
 */
const FirstOccurrenceField: React.FC<SchemaFieldProps> = ({ field, value, onChange, error, values }) => {
  const { t, i18n } = useTranslation();
  const dateStr: string = value ?? '';
  const recurringPattern: string = values?.recurringPattern ?? 'weekly';

  const getDayOfWeek = (d: string): string => {
    if (!d) return '';
    const date = new Date(d + 'T12:00:00');
    return date.toLocaleDateString(i18n.language, { weekday: 'long' });
  };

  const getDayOfMonth = (d: string): number => {
    if (!d) return 0;
    const date = new Date(d + 'T12:00:00');
    return date.getDate();
  };

  const getRecurrenceDescription = (): string => {
    if (!dateStr) return '';
    const dayName = getDayOfWeek(dateStr);
    const dayOfMonth = getDayOfMonth(dateStr);
    switch (recurringPattern) {
      case 'daily':
        return t('templates.everyDay');
      case 'weekly':
        return t('templates.everyWeekday', { dayName });
      case 'biweekly':
        return t('templates.everyOtherWeekday', { dayName });
      case 'monthly':
        return t('templates.monthlyOnDay', { dayOfMonth });
      default:
        return '';
    }
  };

  return (
    <FormField
      htmlFor="sf-firstOccurrence"
      label={t(field.labelKey, field.name)}
      required={field.required}
      error={error}
    >
      <input
        type="date"
        id="sf-firstOccurrence"
        value={dateStr}
        onChange={(e) => onChange(e.target.value)}
      />
      {dateStr && <small className="form-help-text">{getRecurrenceDescription()}</small>}
    </FormField>
  );
};

export default FirstOccurrenceField;
